import React from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { ArrowUpRight } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { KpiCard } from '../components/common/KpiCard';
import { SectionCard } from '../components/common/SectionCard';
import { AiDailyBrief } from '../components/ai/AiDailyBrief';
import { ScoreChip } from '../components/common/ScoreChip';
import { GateStatusPill } from '../components/common/StatusPills';
import { usePlatform } from '../contexts/PlatformContext';
import { MEGA_MAJOR_STAGES, STAGE_META, summariseGate } from '../utils/stages';
import { formatAed, formatDate, daysUntil } from '../utils/format';
import { entityName } from '../data/entities';
export function Dashboard() {
  const {
    applications
  } = usePlatform();
  const live = applications.filter(record => record.status !== 'rejected');
  const megaMajor = live.filter(record => record.track !== 'international');
  const international = live.filter(record => record.track === 'international');
  const committed = live.filter(record => record.stage !== 'assessment').reduce((sum, record) => sum + record.requestedSupportAed, 0);
  const meanScore = live.reduce((sum, record) => sum + record.aiScore, 0) / Math.max(live.length, 1);
  const stageData = MEGA_MAJOR_STAGES.map(stage => ({
    stage: STAGE_META[stage].label,
    Mega: megaMajor.filter(r => r.track === 'mega' && r.stage === stage).length,
    Major: megaMajor.filter(r => r.track === 'major' && r.stage === stage).length
  }));
  const emirates = Array.from(new Set(live.map(record => record.emirate)));
  const emirateData = emirates.map(emirate => ({
    emirate,
    records: live.filter(record => record.emirate === emirate).length
  })).sort((a, b) => b.records - a.records);
  const gatesDue = live.map(record => ({
    record,
    summary: summariseGate(record.gates[record.stage] ?? [])
  })).filter(entry => entry.summary.total > 0 && !entry.summary.cleared).sort((a, b) => daysUntil(a.record.slaDueOn) - daysUntil(b.record.slaDueOn)).slice(0, 5);
  const deadlines = live.filter(record => record.bid?.submissionDeadline).map(record => ({
    record,
    on: record.bid!.submissionDeadline
  })).sort((a, b) => daysUntil(a.on) - daysUntil(b.on));
  return <div data-id="element-1017" data-mp="1jnrawlpqkatu">
      <PageHeader eyebrow="Command dashboard" title="Events support portfolio" description="Live position across the Mega and Major six-stage lifecycle and the International outreach track." actions={<Link to="/applications" className="inline-flex items-center rounded-sm bg-uae-green px-3 py-2 text-[13px] font-medium text-white transition-colors hover:bg-uae-leaf" data-id="element-1019" data-mp="1tjuezx45ejjf">
            Open applications queue
            <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-1020" data-mp="3cjk5do3ux6xg" />
          </Link>} data-id="element-1018" data-mp="1jo138fy96u2p" />

      <div className="space-y-4 p-6" data-id="element-1021" data-mp="3hjgajqoaj4el">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5" data-id="element-1022" data-mp="3hkhmdxd4ruxd">
          <KpiCard label="Records in pipeline" value={String(live.length)} meta={`${megaMajor.length} Mega & Major · ${international.length} International`} data-id="element-1023" data-mp="32myzl0k0jrsk" />
          <KpiCard label="Active bids" value={String(megaMajor.filter(r => r.stage === 'bid').length)} meta="Stage 2 — host-body submission in progress" data-id="element-1024" data-mp="3vbmemee65kxo" />
          <KpiCard label="In delivery" value={String(megaMajor.filter(r => r.stage === 'readiness' || r.stage === 'deliver-operate').length)} meta="Readiness and live operations" data-id="element-1025" data-mp="3lk06iqx4o1z5" />
          <KpiCard label="Support committed" value={formatAed(committed)} meta="Across records past Assessment" data-id="element-1026" data-mp="mvb5dcwdwddg" />
          <KpiCard label="Mean AI score" value={meanScore.toFixed(1)} meta="Weighted across live records" data-id="element-1027" data-mp="2v9rbzfgyotsf" />
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.5fr_1fr]" data-id="element-1028" data-mp="2lefwuw1n5kk5">
          <SectionCard title="Stage distribution — Mega & Major lifecycle" description="Where every record sits across the six governed stages" data-id="element-1029" data-mp="2getkeotn08ce">
            <div className="h-64 w-full" data-id="element-1030" data-mp="3938v4z5qk4hx">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1031" data-mp="394a8jaa8532t">
                <BarChart data={stageData} margin={{
                top: 4,
                right: 8,
                left: -18,
                bottom: 0
              }} data-id="element-1032" data-mp="34c8ylv0j3ofy">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-1033" data-mp="2zdfmo70w45hw" />
                  <XAxis dataKey="stage" tick={{
                  fontSize: 11,
                  fill: 'var(--muted-foreground)'
                }} interval={0} tickLine={false} axisLine={{
                  stroke: 'var(--border)'
                }} data-id="element-1034" data-mp="nfemkbemt0v" />
                  <YAxis allowDecimals={false} tick={{
                  fontSize: 11,
                  fill: 'var(--muted-foreground)'
                }} tickLine={false} axisLine={false} data-id="element-1035" data-mp="47pnr694jj4t" />
                  <ChartTooltip contentStyle={{
                  fontSize: 12,
                  borderRadius: 4,
                  border: '1px solid var(--border)'
                }} data-id="element-1036" data-mp="i5e729njmi7r" />
                  <Legend wrapperStyle={{
                  fontSize: 11
                }} data-id="element-1037" data-mp="r3zog1ha3r6y" />
                  <Bar dataKey="Mega" stackId="a" fill="var(--chart-1)" data-id="element-1038" data-mp="vw984zrsvv0s" />
                  <Bar dataKey="Major" stackId="a" fill="var(--chart-2)" data-id="element-1039" data-mp="10vcf4prccbbe" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          <AiDailyBrief data-id="element-1040" data-mp="15svrfk4hae0p" />
        </div>

        <div className="grid gap-4 xl:grid-cols-2" data-id="element-1041" data-mp="1at0a9gsuzku3">
          <SectionCard title="Gate decisions requiring attention" description="Current-stage gates that are not yet cleared" bodyClassName="p-0" data-id="element-1042" data-mp="1arp7nkb5gj4k">
            <ul className="divide-y divide-border" data-id="element-1043" data-mp="g4gkculncif4">
              {gatesDue.map(({
              record,
              summary
            }) => <li key={record.id} data-id="element-1044" data-mp="uxacpgm0ot17">
                  <Link to={record.track === 'international' ? `/applications/${record.id}` : `/lifecycle/${record.id}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted" data-id="element-1045" data-mp="q4h7krmmziol">
                    <div className="min-w-0" data-id="element-1046" data-mp="1ds8usq9ptg4n">
                      <p className="truncate text-[13px] font-medium" data-id="element-1047" data-mp="18uyxkog34069">{record.eventName}</p>
                      <p className="text-[11px] text-muted-foreground" data-id="element-1048" data-mp="3fl6g8115x6fk">
                        {STAGE_META[record.stage].label} gate · {summary.percent}% ready ·{' '}
                        {summary.outstanding} outstanding
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2" data-id="element-1049" data-mp="1v3nvdr0nfs7u">
                      <ScoreChip score={record.aiScore} data-id="element-1050" data-mp="1v2ujkbmu2ciu" />
                      <GateStatusPill status={summary.outstanding > 0 ? 'outstanding' : 'partial'} data-id="element-1051" data-mp="1gfjb4ily78my" />
                    </div>
                  </Link>
                </li>)}
            </ul>
          </SectionCard>

          <SectionCard title="Records by emirate" description="Distribution of live records across the federation" data-id="element-1052" data-mp="1bfokmg634l2f">
            <div className="h-56 w-full" data-id="element-1053" data-mp="xh6wwig6j8ki">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1054" data-mp="12eyrjk87l8se">
                <BarChart data={emirateData} layout="vertical" margin={{
                top: 0,
                right: 12,
                left: 40,
                bottom: 0
              }} data-id="element-1055" data-mp="12fi74uadzl39">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} data-id="element-1056" data-mp="2wroo69pyow3h" />
                  <XAxis type="number" allowDecimals={false} tick={{
                  fontSize: 11,
                  fill: 'var(--muted-foreground)'
                }} tickLine={false} axisLine={false} data-id="element-1057" data-mp="1jkv5n4euh3p1" />
                  <YAxis type="category" dataKey="emirate" width={110} tick={{
                  fontSize: 11,
                  fill: 'var(--muted-foreground)'
                }} tickLine={false} axisLine={false} data-id="element-1058" data-mp="15vjjo1kibvw7" />
                  <ChartTooltip contentStyle={{
                  fontSize: 12,
                  borderRadius: 4,
                  border: '1px solid var(--border)'
                }} data-id="element-1059" data-mp="ru74hsd67ybd" />
                  <Bar dataKey="records" radius={[0, 2, 2, 0]} data-id="element-1060" data-mp="2m88b4zeyppm0">
                    {emirateData.map((entry, index) => <Cell key={entry.emirate} fill={index === 0 ? 'var(--chart-1)' : 'var(--chart-2)'} data-id="element-1061" data-mp="2otfsvjbz8gnz" data-mpi={index} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>
        </div>

        <SectionCard title="Upcoming host-body submission deadlines" description="Bid windows across active Mega and Major records" bodyClassName="p-0" data-id="element-1062" data-mp="2yjr65whjw27j">
          <ul className="divide-y divide-border" data-id="element-1063" data-mp="25w4tlyqchn8j">
            {deadlines.map(({
            record,
            on
          }) => {
            const days = daysUntil(on);
            return <li key={record.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-id="element-1064" data-mp="2fshxtqjinjhj">
                  <div className="min-w-0" data-id="element-1065" data-mp="2js08zuljcg76">
                    <p className="truncate text-[13px] font-medium" data-id="element-1066" data-mp="3r6v6vp9m1udi">{record.eventName}</p>
                    <p className="text-[11px] text-muted-foreground" data-id="element-1067" data-mp="3v8qjhpwzru6l">
                      {entityName(record.entityId)} · {record.hostBody}
                    </p>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-1068" data-mp="1q3e47ok0b064">
                    <span className="text-[12px] text-muted-foreground" data-id="element-1069" data-mp="1q1t8hvlyhb6o">{formatDate(on)}</span>
                    <span className={`font-mono text-[12px] ${days < 0 ? 'text-muted-foreground' : days < 120 ? 'text-uae-red' : ''}`} data-id="element-1070" data-mp="1uwpfq2zrk54d">
                      {days < 0 ? 'submitted' : `${days}d`}
                    </span>
                  </div>
                </li>;
          })}
          </ul>
        </SectionCard>
      </div>
    </div>;
}